import { type FC, useEffect, useState } from 'react';

import { useParams } from '@tanstack/react-router';
import { omitBy } from 'lodash-es';

import type { AssetsAssetHistoryElasticSchemaWithIdNullable } from '@iconik-io/iconik-api/src/services/assets';
import type { JobsJobElasticSchemaWithId } from '@iconik-io/iconik-api/src/services/jobs';
import { FIELD_TYPES } from '@iconik-io/shared-lib/src/constants/search';
import type { Entities } from '@iconik-io/shared-lib/src/redux/entities/types';
import {
  type SortObject,
  type SortOrder,
  toSortOrder,
} from '@iconik-io/shared-lib/src/utils/sort/types';
import { Icon } from '@iconik-io/uad';

import ErrorBoundary from '../../components/error_boundary/error_boundary';
import { Link } from '../../components/link/link';
import {
  type HistoryOperationData,
  historyOperations,
} from '../../constants/data';
import { getEntityHistory } from '../../ducks/entity_history/thunks';
import { selectDateTimeFormat } from '../../ducks/settings/select_date_time_format';
import { useAppDispatch, useAppSelector } from '../../store/react_redux_typed';
import Select from '../../ui/forms/field_wrappers/select/select';
import SelectUsersFieldWrapper from '../../ui/forms/field_wrappers/select_users/select_users';
import Form from '../../ui/forms/form';
import Label from '../../ui/label/label';
import TableView from '../../ui/table/table';
import type { Column } from '../../ui/table/types';
import formatSnakeCaseTypeToLabel from '../../utils/format_snake_case_type';
import { selectUserRoles } from '../../utils/get_user_roles';

import styles from './entity_history.module.css';

type OperationsType = { [key: string]: HistoryOperationData };

const makeFallbackOperation = (operationType: string): HistoryOperationData => {
  const label = formatSnakeCaseTypeToLabel(operationType);
  return { value: operationType, label, bsStyle: 'info' };
};

const operations: OperationsType = {};
historyOperations.forEach((operation) => {
  operations[operation.value] = operation;
});

type AutomationIconLinkProps = {
  item: AssetsAssetHistoryElasticSchemaWithIdNullable;
  jobs: Entities<JobsJobElasticSchemaWithId>;
};

const getJobAutomationId = (
  jobsState: Entities<JobsJobElasticSchemaWithId>,
  job_id: string | null | undefined,
): string | undefined => {
  if (!job_id) {
    return;
  }

  const automationId = jobsState[job_id]?.metadata?.automation_id;
  return typeof automationId === 'string' ? automationId : undefined;
};

const AutomationIconLink = ({ item, jobs }: AutomationIconLinkProps) => {
  const automationId = getJobAutomationId(jobs, item.job_id);

  if (automationId) {
    return (
      <Link
        to={`/admin/automations/${automationId}`}
        title="Open automation settings"
      >
        <Icon name="automations" />
      </Link>
    );
  } else {
    return <span />;
  }
};

const columns: Column<AssetsAssetHistoryElasticSchemaWithIdNullable>[] = [
  {
    name: 'user_id',
    type: FIELD_TYPES.USER,
    label: 'User',
    style: { width: '1%' },
    sortable: true,
  },
  {
    name: 'operation_description',
    type: FIELD_TYPES.TEXT,
    label: 'Description',
    style: { width: '60%' },
  },
  {
    name: 'operation_type',
    type: FIELD_TYPES.CUSTOM_COMPONENT,
    label: 'Operation',
    component: ({ item }) => {
      const operation =
        operations[item.operation_type] ||
        makeFallbackOperation(item.operation_type);
      return <Label variant={operation.bsStyle}>{operation.label}</Label>;
    },
    style: { width: '10%' },
    sortable: true,
  },
  {
    name: 'date_created',
    type: FIELD_TYPES.DATE,
    label: 'Created',
    style: { width: '40%' },
    sortable: true,
  },
];

type Params = {
  per_page: number;
  // Dropped from the query when empty, so absent rather than blank.
  sort?: string;
  operation_type?: string;
  user_id?: string;
  page?: number;
};

export const EntityHistoryPanel: FC = () => {
  const [params, setParams] = useState<Params>({
    per_page: 1000,
    sort: 'date_created,desc',
  });

  const dispatch = useAppDispatch();

  const { id: entityId } = useParams({ strict: false });
  const history = useAppSelector((state) =>
    entityId ? state.assetHistoryReducer.entityHistory[entityId] : undefined,
  );
  const jobs = useAppSelector((state) => state.entities.jobs);
  const dateTimeFormat = useAppSelector(selectDateTimeFormat);
  const roles = useAppSelector(selectUserRoles);

  useEffect(() => {
    const queryParams = {
      per_page: 1000,
      sort: 'date_created,desc',
    };
    if (!entityId) {
      return;
    }
    dispatch(getEntityHistory(entityId, queryParams));
    setParams(queryParams);
  }, [entityId]);

  const onScrollEnds = (): void => {
    const { page } = history ?? {};
    if (page) {
      update({ page: page + 1 });
    }
  };

  const onFilterOperation = ({ operation_type }: Partial<Params>): void => {
    update({ operation_type });
  };

  const onFilterUser = ({ user_id }: Partial<Params>): void => {
    update({ user_id });
  };

  const onClickSort = (
    name: string,
    sortOrder: SortOrder | undefined,
  ): void => {
    const newOrder = sortOrder === 'desc' ? 'asc' : 'desc';
    update({ sort: `${name},${newOrder}` });
  };

  const update = (paramsUpdate: Partial<Params>): void => {
    const updatedParams: Params = {
      ...omitBy(
        { ...params, ...paramsUpdate, page: paramsUpdate.page || 1 },
        (value?: string | number) => !value,
      ),
      per_page: 1000,
    };

    if (entityId) {
      dispatch(getEntityHistory(entityId, updatedParams));
    }
    setParams(updatedParams);
  };

  const { objects = [] } = history ?? {};

  const shouldShowAutomationsColumn = objects.some((item) =>
    getJobAutomationId(jobs, item.job_id),
  );

  const [sortName = '', sortDirection] = (params.sort || '').split(',');
  const order: SortObject = { [sortName]: toSortOrder(sortDirection) };

  let renderColumns = [...columns];

  if (roles.canReadJobs) {
    renderColumns = [
      ...columns,
      {
        name: 'job_id',
        label: ' ',
        type: FIELD_TYPES.CUSTOM_COMPONENT,
        component: ({ item }) =>
          item.job_id ? (
            <Link
              to={`/admin/jobs/${item.job_id}?job=${item.job_id}`}
              aria-label="Open job details"
            >
              <Icon name="jobs" />
            </Link>
          ) : (
            <span />
          ),
        style: { width: '1%' },
        sortable: false,
      },
    ];

    if (shouldShowAutomationsColumn && roles.canReadAutomations) {
      renderColumns.push({
        name: 'automation_id',
        label: ' ',
        type: FIELD_TYPES.CUSTOM_COMPONENT_WITH_PROPS,
        component: AutomationIconLink,
        componentProps: { jobs },
        style: { width: '1%' },
        sortable: false,
      });
    }
  }

  return (
    <ErrorBoundary>
      <div className={styles.wrapper}>
        <div>
          <Form onChange={onFilterOperation}>
            <Select
              className={styles.field}
              canClear
              name="operation_type"
              options={historyOperations}
              placeholder="Operation"
            />
          </Form>
          <Form onChange={onFilterUser}>
            <SelectUsersFieldWrapper
              className={styles.field}
              canClear
              isMulti
              name="user_id"
              placeholder="User"
              plainMultiValues
            />
          </Form>
        </div>
        <div className={styles.table_wrapper}>
          <TableView
            tbodyClassName={styles.tbody}
            renderColumns={renderColumns}
            items={objects}
            dateFormat={dateTimeFormat}
            sorting={order}
            onSort={onClickSort}
            onBottomVisible={onScrollEnds}
          />
        </div>
      </div>
    </ErrorBoundary>
  );
};
